/**
 * Storage Initialization Composable
 *
 * Initializes SQL storage with authentication service once auth is ready.
 * Call this from the root App.vue after auth initialization.
 */

import { watch } from 'vue'
import { reportError } from '@/lib/error-reporting'
import { useAuthStore } from '@/stores/auth.store'
import { useAssumptionsStore } from '@/stores/assumptions.store'
import { usePortMappingStore } from '@/stores/portMapping.store'
import { useMasterEquipmentStore } from '@/stores/master-equipment.store'
import { AuthServiceAdapter } from '@/services/storage/auth-service-adapter'
import { createStorageAdapter } from '@/services/storage/factory'
import * as SurveyStorage from '@/services/survey-storage.service'
import type { SurveyAssumptions } from '@/types/assumptions.types'
import type { SurveyPortMapping } from '@/types/portMapping.types'
import type { SurveyMasterEquipmentSettings } from '@/types/master-equipment.types'

let storageInitialized = false

/**
 * Initialize storage with auth service when authentication is ready
 */
export function useStorageInitialization() {
  const authStore = useAuthStore()
  const assumptionsStore = useAssumptionsStore()
  const portMappingStore = usePortMappingStore()
  const masterEquipmentStore = useMasterEquipmentStore()

  // Watch for auth initialization
  watch(
    () => authStore.isAuthenticated,
    async isAuth => {
      if (isAuth && !storageInitialized) {
        try {
          // Get the IAuthService from the auth store
          const authService = authStore.getAuthService()

          if (!authService) {
            if (import.meta.env.DEV) {
              console.warn(
                '[StorageInit] Auth service not available, SQL storage will use localStorage fallback'
              )
            }
            return
          }

          // Create adapter that bridges IAuthService to AuthService interface
          const adapter = new AuthServiceAdapter(authService)

          // Initialize storage with auth (force re-initialization to switch from localStorage to SQL)
          SurveyStorage.initializeStorage(adapter, true)

          // Create SQL storage adapters for each store
          const assumptionsAdapter = createStorageAdapter<SurveyAssumptions>('assumptions', adapter)
          const portMappingAdapter = createStorageAdapter<SurveyPortMapping>(
            'port-mappings',
            adapter
          )
          const masterEquipmentAdapter = createStorageAdapter<SurveyMasterEquipmentSettings>(
            'master-equipment',
            adapter
          )

          // Set adapters on stores
          assumptionsStore.setStorageAdapter(assumptionsAdapter)
          portMappingStore.setStorageAdapter(portMappingAdapter)
          masterEquipmentStore.setStorageAdapter(masterEquipmentAdapter)

          storageInitialized = true

          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log('[StorageInit] ✅ SQL storage initialized with authentication')
            // eslint-disable-next-line no-console
            console.log('[StorageInit] ✅ All Pinia stores connected to SQL adapters')
          }
        } catch (error) {
          reportError(error, { where: 'useStorageInitialization.watch' })
        }
      }
    },
    { immediate: true }
  )

  return {
    isInitialized: () => storageInitialized,
  }
}
